import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, TicketPercent } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState, ConfirmDialog } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EMPTY = { code: "", type: "percent", value: "", min_order: 0, active: true };

export default function StoreCoupons() {
  const [coupons, setCoupons] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/store-admin/coupons").then((r) => setCoupons(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, value: Number(form.value), min_order: Number(form.min_order) };
    try {
      if (dialog.mode === "create") await api.post("/store-admin/coupons", payload);
      else await api.patch(`/store-admin/coupons/${dialog.coupon.id}`, payload);
      toast.success("Coupon saved"); setDialog(null); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="store-coupons">
      <PageHeader title="Coupons & Discounts" subtitle="Create offers your customers can apply at checkout.">
        <Button data-testid="add-coupon-button" onClick={() => { setForm(EMPTY); setDialog({ mode: "create" }); }} className="rounded-full font-bold active:scale-95 transition-transform">
          <Plus className="h-4 w-4 mr-2" /> New Coupon
        </Button>
      </PageHeader>

      {!coupons ? <LoadingRows /> : coupons.length === 0 ? (
        <EmptyState icon={TicketPercent} title="No coupons" body="Create a coupon code like SAVE10 to boost conversions." />
      ) : (
        <TableShell testid="coupons-table" head={["Code", "Discount", "Min Order", "Status", "Actions"]}>
          {coupons.map((c) => (
            <TR key={c.id} testid={`coupon-row-${c.id}`}>
              <TD><span className="font-mono font-bold">{c.code}</span></TD>
              <TD className="font-bold">{c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}</TD>
              <TD className="text-sm">{c.min_order ? `₹${c.min_order}+` : "None"}</TD>
              <TD><StatusBadge status={c.active ? "active" : "suspended"} /></TD>
              <TD>
                <div className="flex gap-1">
                  <button data-testid={`edit-coupon-${c.id}`} onClick={() => { setForm(c); setDialog({ mode: "edit", coupon: c }); }} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"><Pencil className="h-4 w-4" /></button>
                  <ConfirmDialog testid={`delete-coupon-${c.id}`} title="Delete coupon?" description={`"${c.code}" will stop working immediately.`}
                    onConfirm={async () => { await api.delete(`/store-admin/coupons/${c.id}`); toast.success("Coupon deleted"); load(); }}
                    trigger={<button className="h-8 w-8 rounded-full hover:bg-destructive/10 text-destructive flex items-center justify-center transition-colors"><Trash2 className="h-4 w-4" /></button>} />
                </div>
              </TD>
            </TR>
          ))}
        </TableShell>
      )}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog?.mode === "create" ? "New Coupon" : "Edit Coupon"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="coupon-form">
            <div className="space-y-1.5"><Label className="overline-label">Code</Label>
              <Input data-testid="coupon-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SAVE10" className="font-mono" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="overline-label">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percentage</SelectItem><SelectItem value="fixed">Fixed amount</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="overline-label">Value {form.type === "percent" ? "(%)" : "(₹)"}</Label>
                <Input data-testid="coupon-value" type="number" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Minimum order (₹)</Label>
              <Input data-testid="coupon-min-order" type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} /></div>
            <div className="flex items-center justify-between border border-border rounded-md p-4">
              <span className="text-sm font-bold">Active</span>
              <Switch data-testid="coupon-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <Button data-testid="coupon-save-button" disabled={busy} className="w-full h-11 rounded-full font-bold">{busy ? "Saving…" : "Save Coupon"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
